import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, X, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

const DemoCallForSolutionsQuebecBountyDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [isBounty, setIsBounty] = useState<string | null>(null);
  const [isAreaExpanded, setIsAreaExpanded] = useState(false);
  const [isBountyExpanded, setIsBountyExpanded] = useState(false);
  const [isBountyDetailsExpanded, setIsBountyDetailsExpanded] = useState(false);
  
  const [bountyDetails, setBountyDetails] = useState<any>(null);
  
  const [callTitle, setCallTitle] = useState("Quebec-Houston Port Decarbonization Initiative");
  const [requirements, setRequirements] = useState(
    "Objective: Develop innovative solutions for decarbonizing port infrastructure in the Gulf region through Quebec-Houston collaboration. Priority areas include smart maritime logistics, electrified ground equipment, and clean battery systems. Solutions should demonstrate measurable emission reductions, commercial viability, and potential for cross-border deployment.\n\nProject Duration: 18 months (Target: Pilot deployment by Q2 2026)\n\nCollaboration Framework: Quebec Government Office - Houston, Port of Houston, Texas GLO Innovation"
  );
  const [additionalMaterials, setAdditionalMaterials] = useState(
    "https://quebecgovhouston.org/port-decarbonization-initiative\nhttps://portofhouston.com/sustainability/environmental-initiatives"
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  // Update these to preassigned values for Quebec context
  const [leadAssignment, setLeadAssignment] = useState<string>("assign");
  const [collaboratorAssignment, setCollaboratorAssignment] = useState<string>("assign");
  const [reviewerAssignment, setReviewerAssignment] = useState<string>("assign");
  const [coordinatorAssignment, setCoordinatorAssignment] = useState<string>("assign");
  
  // Update with Quebec-specific preassigned partners
  const [leadPartner, setLeadPartner] = useState<string>("Quebec Delegation – Houston");
  const [collaboratorPartner, setCollaboratorPartner] = useState<string>("Lion Electric");
  const [reviewerPartner, setReviewerPartner] = useState<string>("McGill University");
  const [coordinatorPartner, setCoordinatorPartner] = useState<string>("Eglantine Kuntz");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("eglantine.pauvarelmoss@quebecgovhouston.org");
  const [message, setMessage] = useState(
    `Bonjour/Hello,\n\nWe're excited to launch this Quebec-Houston port decarbonization initiative.\n\nProject Type: Quebec Government-led, Cross-border Partnership, iSPAI-powered\n\nParticipants:\n\nComposer (Quebec Delegation): Guide overall strategy and cross-border coordination\n\nContributors (Quebec Companies): Provide innovative solutions in maritime logistics, electric equipment, and battery systems\n\nConfirmers (Academic Partners): Validate solutions for environmental impact, commercial viability, and regulatory compliance\n\nCreator: Facilitate Quebec-Houston collaboration and ensure deliverable quality\n\nStakeholders: Quebec Government Office - Houston, Port of Houston, Texas GLO Innovation\n\nLet's discuss the next steps for implementation.`
  );

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const quebecPartners = [
    "Eglantine Kuntz",
    "Quebec Delegation – Houston",
    "Hydro-Québec International",
    "Lion Electric",
    "Boralex Energy",
    "McGill University",
    "Université Laval"
  ];

  useEffect(() => {
    if (location.state) {
      setSelectedArea(location.state.selectedArea || "");
      setIsBounty(location.state.isBounty || null);
      
      if (location.state.bountyDetails) {
        setBountyDetails(location.state.bountyDetails);
      }
    }
  }, [location]);

  const handleBack = () => {
    navigate("/demo-call-for-solutions-quebec/bounty", { state: { selectedArea } });
  };

  const handleSendMessage = () => {
    setShowConfirmationDialog(true);
  };
  
  const handleConfirmSend = () => {
    console.log("Quebec Demo - Sending message to coordinator:", { email, message });
    
    const updatedBountyDetails = {
      ...bountyDetails,
      roleAssignments: {
        lead: {
          status: leadAssignment,
          assignedTo: leadAssignment === "assign" ? leadPartner : "Open"
        },
        collaborator: {
          status: collaboratorAssignment,
          assignedTo: collaboratorAssignment === "assign" ? collaboratorPartner : "Open"
        },
        reviewer: {
          status: reviewerAssignment,
          assignedTo: reviewerAssignment === "assign" ? reviewerPartner : "Open"
        },
        coordinator: {
          status: coordinatorAssignment,
          assignedTo: coordinatorAssignment === "assign" ? coordinatorPartner : "Open"
        }
      }
    };
    
    setShowConfirmationDialog(false);
    
    setEmail("");
    setMessage("");
    
    toast({
      title: "Message Sent Successfully",
      description: "Your Quebec Port Decarbonization Call for Solutions has been submitted and will appear in your mySPAI profile.",
    });
    
    navigate("/demo-creator-rewards-quebec", { 
      state: { 
        newCall: {
          title: callTitle,
          area: selectedArea,
          type: isBounty === "yes" ? "Quebec Innovation Grant" : "Quebec Call for Solutions",
          date: new Date().toLocaleDateString(),
          bountyDetails: updatedBountyDetails,
          isQuebec: true
        }
      }
    });
  };

  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const totalFiles = [...selectedFiles, ...newFiles];
      
      if (totalFiles.length > 5) {
        toast({
          title: "Maximum Files Exceeded",
          description: "You can upload a maximum of 5 files.",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedFiles(totalFiles);
    }
  };
  
  const removeFile = (index: number) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };
  
  const generateEmailPreview = () => {
    return (
      <div className="space-y-4 text-sm">
        <div>
          <p><strong>{t('quebec.email.to')}:</strong> {t('quebec.email.projectCoordinator')}</p>
          <p><strong>{t('quebec.email.from')}:</strong> {email}</p>
          <p><strong>{t('quebec.email.subject')}:</strong> {t('quebec.email.newQuebecCall')}: {callTitle}</p>
        </div>
        
        <div className="border-t pt-2">
          <p className="mb-2"><strong>{t('quebec.email.callDetails')}:</strong></p>
          <p><strong>{t('quebec.email.title')}:</strong> {callTitle}</p>
          <p><strong>{t('quebec.email.expertiseArea')}:</strong> {selectedArea}</p>
          <p><strong>{t('quebec.email.type')}:</strong> {isBounty === "yes" ? t('quebec.email.innovationGrant') : t('quebec.email.callForSolutions')}</p>
          
          {isBounty === "yes" && bountyDetails && (
            <div className="mt-2">
              <p><strong>{t('quebec.email.grantAmount')}:</strong> {bountyDetails.bountyAmount || t('quebec.bountyDetails.notSpecified')}</p>
              <p><strong>{t('quebec.email.grantType')}:</strong> {bountyDetails.bountyType || t('quebec.bountyDetails.notSpecified')}</p>
              
              {bountyDetails.attribution && (
                <div className="mt-2">
                  <p><strong>{t('quebec.email.roleAssignments')}:</strong></p>
                  <ul className="list-disc pl-5 mt-1">
                    <li>{t('quebec.email.composer')} ({bountyDetails.attribution.leadPercentage || 0}%): {leadPartner}</li>
                    <li>{t('quebec.email.contributor')} ({bountyDetails.attribution.collaboratorPercentage || 0}%): {collaboratorPartner}</li>
                    <li>{t('quebec.email.confirmer')} ({bountyDetails.attribution.reviewerPercentage || 0}%): {reviewerPartner}</li>
                    <li>{t('quebec.email.creator')} ({bountyDetails.attribution.coordinatorPercentage || 0}%): {coordinatorPartner}</li>
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-2">
            <p><strong>{t('quebec.email.requirements')}:</strong></p>
            <p className="whitespace-pre-wrap">{requirements}</p>
          </div>
          
          {additionalMaterials && (
            <div className="mt-2">
              <p><strong>{t('quebec.email.additionalMaterials')}:</strong></p>
              <p className="whitespace-pre-wrap">{additionalMaterials}</p>
            </div>
          )}
          
          {selectedFiles.length > 0 && (
            <div className="mt-2">
              <p><strong>{t('quebec.email.attachedFiles')}:</strong></p>
              <ul className="list-disc pl-5">
                {selectedFiles.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="mt-4 pt-2 border-t">
            <p><strong>{t('quebec.email.messageFromConsumer')}:</strong></p>
            <p className="whitespace-pre-wrap">{message}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/quebec-channel")}
        />
      </div>

      <div className="w-full max-w-2xl space-y-6">
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsAreaExpanded(!isAreaExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">{t('quebec.bounty.selectExpertiseArea')}</h2>
              <p className="text-gray-600 mt-1">{t('quebec.bounty.selected')}: {selectedArea}</p>
            </div>
            <div>
              {isAreaExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsBountyExpanded(!isBountyExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">{t('quebec.bountyDetails.innovationGrant')}</h2>
              <p className="text-gray-600 mt-1">{t('quebec.bounty.isInnovationGrant')} {isBounty === "yes" ? t('quebec.bounty.yes') : t('quebec.bounty.no')}</p>
            </div>
            <div>
              {isBountyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        {bountyDetails && (
          <Card className="border border-gray-200 overflow-hidden">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => setIsBountyDetailsExpanded(!isBountyDetailsExpanded)}
            >
              <div>
                <h2 className="text-xl font-semibold">{t('quebec.bountyDetails.grantDetails')}</h2>
                <p className="text-gray-600 mt-1">
                  {t('quebec.bountyDetails.amount')}: {bountyDetails.bountyAmount || t('quebec.bountyDetails.notSpecified')}, 
                  {t('quebec.bountyDetails.type')}: {bountyDetails.bountyType || t('quebec.bountyDetails.notSpecified')}
                </p>
              </div>
              <div>
                {isBountyDetailsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
            
            {isBountyDetailsExpanded && (
              <div className="p-4 border-t border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">{t('quebec.bountyDetails.collaborationFramework')}</h3>
                    <p className="text-sm text-gray-600 mt-1">{bountyDetails.escrowConditions || t('quebec.bountyDetails.crossBorderTerms')}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">{t('quebec.bountyDetails.successCriteria')}</h3>
                    <p className="text-sm text-gray-600 mt-1">{bountyDetails.releaseConditions || t('quebec.bountyDetails.environmentalValidation')}</p>
                  </div>
                  
                  {bountyDetails.attribution && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">{t('quebec.bountyDetails.partnershipRoles')}</h3>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <p className="text-sm text-gray-600">{t('quebec.bountyDetails.solutionLead')}: {bountyDetails.attribution.leadPercentage || 0}%</p>
                        <p className="text-sm text-gray-600">{t('quebec.bountyDetails.collaborator')}: {bountyDetails.attribution.collaboratorPercentage || 0}%</p>
                        <p className="text-sm text-gray-600">{t('quebec.bountyDetails.technicalReviewer')}: {bountyDetails.attribution.reviewerPercentage || 0}%</p>
                        <p className="text-sm text-gray-600">{t('quebec.bountyDetails.coordinator')}: {bountyDetails.attribution.coordinatorPercentage || 0}%</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">{t('quebec.bountyDetails.callDetails')}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quebec.bountyDetails.callTitle')}
                </label>
                <Input 
                  value={callTitle} 
                  onChange={(e) => setCallTitle(e.target.value)}
                  placeholder={t('quebec.bountyDetails.enterCallTitle')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quebec.bountyDetails.requirementsObjectives')}
                </label>
                <Textarea 
                  value={requirements} 
                  onChange={(e) => setRequirements(e.target.value)}
                  placeholder={t('quebec.bountyDetails.requirementsPlaceholder')}
                  rows={6}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quebec.bountyDetails.additionalMaterials')}
                </label>
                <Textarea 
                  value={additionalMaterials} 
                  onChange={(e) => setAdditionalMaterials(e.target.value)}
                  placeholder={t('quebec.bountyDetails.addRelevantLinks')}
                  rows={3}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">{t('quebec.bountyDetails.sendToCoordinator')}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quebec.bountyDetails.coordinatorEmail')}
                </label>
                <Input 
                  type="email"
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('quebec.bountyDetails.enterCoordinatorEmail')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quebec.bountyDetails.message')}
                </label>
                <Textarea 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('quebec.bountyDetails.messagePlaceholder')}
                  rows={8}
                />
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="border-gray-300"
              >
                {t('ui.back')}
              </Button>
              <Button 
                onClick={handleSendMessage}
                disabled={!email || !message}
                className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
              >
                {t('quebec.bountyDetails.sendQuebecCall')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('quebec.bountyDetails.confirmSubmission')}</DialogTitle>
            <DialogDescription>
              {t('quebec.bountyDetails.reviewDetails')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-md p-4 bg-gray-50">
            {generateEmailPreview()}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmationDialog(false)}
            >
              {t('ui.cancel')}
            </Button>
            <Button 
              onClick={handleConfirmSend}
              className="bg-[#14213d] hover:bg-[#0d1829] text-white"
            >
              {t('demo.confirmSend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoCallForSolutionsQuebecBountyDetails;